import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CriminalDocumentRow } from '../../../types/criminalCaseRequest';
import * as criminalService from '../../../services/criminalCaseRequests';
import DocumentPreviewModal from '../../../components/ui/DocumentPreviewModal';
import { setFlashToast } from '../../../utils/flashToast';
import { formatReviewFileMeta } from '../../../utils/dateFormat';
import { displayClientFileName } from '../../../utils/fileDisplayName';

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function WarnIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

function CircleIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <circle cx="12" cy="12" r="9" />
    </svg>
  );
}

function FileIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

type Props = {
  documents: CriminalDocumentRow[];
};

export function CriminalReviewDocumentsTable({ documents }: Props) {
  const { t } = useTranslation();
  const [previewDoc, setPreviewDoc] = useState<CriminalDocumentRow | null>(null);

  const downloadFile = async (doc: CriminalDocumentRow) => {
    if (!doc.fileName) return;
    const r = await criminalService.downloadCaseFile(doc.fileName);
    if (!r.success) {
      setFlashToast({ message: r.message ?? 'Download failed.', variant: 'error' });
      return;
    }
    const name = displayClientFileName(doc.originalFileName, doc.fileName);
    const url = URL.createObjectURL(r.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (documents.length === 0) {
    return <p className="ccr-review-empty">{t('wizard.documents.noDocumentsDeclared')}</p>;
  }

  return (
    <>
      <div className="ccr-review-doc-wrap">
        <table className="ccr-review-doc-table">
          <thead>
            <tr>
              <th>{t('wizard.documents.documentType')}</th>
              <th>{t('wizard.documents.status')}</th>
              <th>{t('wizard.documents.fileOrReason')}</th>
            </tr>
          </thead>
          <tbody>
            {documents.map((doc) => {
              const uploaded = Boolean(doc.fileName);
              const displayName = displayClientFileName(doc.originalFileName, doc.fileName);
              const fileMeta = formatReviewFileMeta(doc.fileSize, doc.uploadTime);
              const skipReason = doc.skipReason?.trim() ?? '';

              let statusClass = 'is-optional';
              let StatusIcon = CircleIcon;
              if (uploaded) {
                statusClass = 'is-uploaded';
                StatusIcon = CheckIcon;
              } else if (doc.mandatory) {
                statusClass = 'is-pending';
                StatusIcon = WarnIcon;
              }

              return (
                <tr key={doc.id}>
                  <td className="ccr-review-doc-type">{doc.documentName}</td>
                  <td>
                    <span className={`ccr-review-status ${statusClass}`}>
                      <StatusIcon />
                      {uploaded ? t('wizard.documents.uploaded') : t('wizard.documents.notUploaded')}
                    </span>
                  </td>
                  <td>
                    {uploaded ? (
                      <div className="ccr-review-file-cell">
                        <span className="ccr-review-file-ico">
                          <FileIcon />
                        </span>
                        <div className="ccr-review-file-body">
                          <button
                            type="button"
                            className="ccr-review-file-name"
                            onClick={() => setPreviewDoc(doc)}
                          >
                            {displayName}
                          </button>
                          {fileMeta ? <span className="ccr-review-file-meta">{fileMeta}</span> : null}
                        </div>
                        <div className="ccr-review-file-acts">
                          <button
                            type="button"
                            className="ccr-review-file-act"
                            aria-label={`Preview ${displayName}`}
                            data-tip="Preview"
                            onClick={() => setPreviewDoc(doc)}
                          >
                            <EyeIcon />
                          </button>
                          <button
                            type="button"
                            className="ccr-review-file-act"
                            aria-label={`Download ${displayName}`}
                            data-tip="Download"
                            onClick={() => void downloadFile(doc)}
                          >
                            <DownloadIcon />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <span className={`ccr-review-reason${skipReason ? ' is-reason' : ''}`}>
                        {skipReason || '—'}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <DocumentPreviewModal
        isOpen={Boolean(previewDoc)}
        doc={previewDoc}
        onClose={() => setPreviewDoc(null)}
      />
    </>
  );
}
